import React, { useState, useCallback, useRef, useEffect } from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Select, { SelectOption } from '@amzn/meridian/select';

import { ERROR_MESSAGES } from './flowPlannerConstants';
import { createOptimizedPlan, fetchPlan } from './flowPlannerApi';
import { initializeModel } from './FlowPlannerModel';
import CopyToClipboardButton from '../../commonComponents/CopyToClipboardButton';
import { TEXTS, PLAN_STATUS } from '../../utils/constants';
import {
  publishFlowPlannerCountMetric,
  publishFlowPlannerTimeMetric,
  FLOW_PLANNER_METRICS as M
} from '../../utils/metricUtil';

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_ATTEMPTS = 60;

const COMPARISON_METRICS = [
  { key: 'totalTPH', label: 'Total TPH', lowerIsBetter: false },
  { key: 'totalStowHC', label: 'Total Stow Headcount', lowerIsBetter: true },
  { key: 'totalInductHC', label: 'Total Induct Headcount', lowerIsBetter: true },
  { key: 'totalPickHC', label: 'Total Pick Headcount', lowerIsBetter: true }
];

const HC_GROUP_KEYS = {
  totalInductHC: 'INDUCT_GROUP',
  totalStowHC: 'STOW',
  totalPickHC: 'OUTBOUND'
};

/**
 * Extract comparison metrics from model outputs, per cycle and total.
 * Returns { total: { totalTPH, ... }, cycles: { cycleName: { totalTPH, ... } }, cycleNames: string[] }
 */
function extractMetricsFromGridData(gridData) {
  const empty = { totalTPH: 0, totalStowHC: 0, totalInductHC: 0, totalPickHC: 0 };
  if (!gridData) return { total: { ...empty }, cycles: {}, cycleNames: [] };

  const cycles = {};
  const total = { ...empty };
  const cycleNames = Object.keys(gridData).sort();

  cycleNames.forEach((cycleName) => {
    const cycleData = gridData[cycleName];
    const cycleMetrics = { ...empty };
    cycleMetrics.totalTPH = Math.round(cycleData.tph || 0);
    (cycleData.rows || []).forEach((row) => {
      Object.entries(HC_GROUP_KEYS).forEach(([metricKey, groupKey]) => {
        if (row.key === groupKey) {
          cycleMetrics[metricKey] += (row.slots || []).reduce((sum, slot) => sum + (slot.headcount || 0), 0);
        }
      });
    });
    cycleMetrics.totalStowHC = Math.round(cycleMetrics.totalStowHC * 100) / 100;
    cycleMetrics.totalInductHC = Math.round(cycleMetrics.totalInductHC * 100) / 100;
    cycleMetrics.totalPickHC = Math.round(cycleMetrics.totalPickHC * 100) / 100;
    cycles[cycleName] = cycleMetrics;

    total.totalTPH += cycleMetrics.totalTPH;
    total.totalStowHC += cycleMetrics.totalStowHC;
    total.totalInductHC += cycleMetrics.totalInductHC;
    total.totalPickHC += cycleMetrics.totalPickHC;
  });

  total.totalTPH = Math.round(total.totalTPH);
  total.totalStowHC = Math.round(total.totalStowHC * 100) / 100;
  total.totalInductHC = Math.round(total.totalInductHC * 100) / 100;
  total.totalPickHC = Math.round(total.totalPickHC * 100) / 100;

  return { total, cycles, cycleNames };
}

/**
 * Resolves the default preset value from the configured list.
 * Priority: enabled & default=true → first enabled → null (no preset selectable).
 */
function resolveDefaultPreset(presets) {
  const enabledDefault = presets.find((p) => p.default && p.enabled);
  if (enabledDefault) return enabledDefault.value;
  const firstEnabled = presets.find((p) => p.enabled);
  if (firstEnabled) return firstEnabled.value;
  return null;
}

const MathematicalOptimizerModal = ({
  isOpen,
  onClose,
  date,
  model,
  dependentPlanId,
  optimizerPresets,
  onUpdatePlan
}) => {
  const presets = optimizerPresets || [];
  const hasPresets = presets.some((p) => p.enabled);
  const defaultPreset = resolveDefaultPreset(presets);
  const [selectedPreset, setSelectedPreset] = useState(defaultPreset);
  const [isCalculating, setIsCalculating] = useState(false);
  const [optimizedPlanData, setOptimizedPlanData] = useState(null);
  const [optimizedPlanId, setOptimizedPlanId] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState('total');
  const [error, setError] = useState(null);
  const generationRef = useRef(0);
  const hasAutoCalculated = useRef(false);

  const resetState = useCallback(() => {
    generationRef.current++;
    setSelectedPreset(defaultPreset);
    setIsCalculating(false);
    setOptimizedPlanData(null);
    setOptimizedPlanId(null);
    setComparisonData(null);
    setSelectedCycle('total');
    setError(null);
  }, [defaultPreset]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleCancel = useCallback(() => {
    publishFlowPlannerCountMetric(M.CANCEL_OPTIMIZER);
    handleClose();
  }, [handleClose]);

  // Cancel polling on unmount
  useEffect(
    () => () => {
      generationRef.current++;
    },
    []
  );

  const pollForPlan = useCallback(async (planId, generation) => {
    for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
      if (generationRef.current !== generation) return null;
      const plan = await fetchPlan(planId, { resolution: 'USER_DEFINED' });
      if (plan.status === PLAN_STATUS.PENDING_APPROVAL || plan.status === PLAN_STATUS.PENDING_USER_INPUT) return plan;
      if (plan.status === PLAN_STATUS.FAILED) throw new Error(plan.errorMessage || 'Plan creation failed');
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    throw new Error('Plan creation timed out. Please try again.');
  }, []);

  const handleCalculate = useCallback(async () => {
    const thisGen = ++generationRef.current;
    const startTime = Date.now();
    publishFlowPlannerCountMetric(M.CALCULATE_OPTIMIZED_PLAN);
    setIsCalculating(true);
    setError(null);
    setComparisonData(null);
    setOptimizedPlanData(null);
    setOptimizedPlanId(null);

    try {
      const res = await createOptimizedPlan(date, model?.overrides, model?.rawInput, selectedPreset, dependentPlanId);
      publishFlowPlannerTimeMetric(M.CREATE_OPTIMIZED_PLAN, startTime);
      if (!res?.planId) {
        throw new Error(ERROR_MESSAGES.CREATE_PLAN);
      }
      // Surface the created plan id while it is still being generated (polling) and in the results.
      setOptimizedPlanId(res.planId);

      const plan = await pollForPlan(res.planId, thisGen);
      if (generationRef.current !== thisGen || !plan) return;

      publishFlowPlannerTimeMetric(M.CREATE_OPTIMIZED_PLAN_E2E, startTime);

      const planStr = JSON.stringify(plan);
      setOptimizedPlanData(planStr);

      const currentMetrics = extractMetricsFromGridData(model?.outputs?.gridData);
      const optimizedModel = initializeModel(planStr);
      const optimizedMetrics = extractMetricsFromGridData(optimizedModel?.outputs?.gridData);
      setComparisonData({ current: currentMetrics, optimized: optimizedMetrics });
      setSelectedCycle('total');
    } catch (e) {
      if (generationRef.current !== thisGen) return;
      publishFlowPlannerCountMetric(M.CREATE_OPTIMIZED_PLAN_FAILED);
      setError(e.message || ERROR_MESSAGES.CREATE_PLAN);
    } finally {
      if (generationRef.current === thisGen) setIsCalculating(false);
    }
  }, [date, model, selectedPreset, pollForPlan, dependentPlanId]);

  // When no presets are configured, skip the selection step and calculate immediately on open.
  // hasAutoCalculated guards against re-firing when handleCalculate's identity changes mid-session.
  useEffect(() => {
    if (!isOpen) {
      hasAutoCalculated.current = false;
      return;
    }
    if (!hasPresets && !hasAutoCalculated.current) {
      hasAutoCalculated.current = true;
      handleCalculate();
    }
  }, [isOpen, hasPresets, handleCalculate]);

  const handleUpdatePlan = useCallback(() => {
    publishFlowPlannerCountMetric(M.UPDATE_PLAN_FROM_OPTIMIZER);
    if (optimizedPlanData) {
      onUpdatePlan(optimizedPlanData);
    }
    handleClose();
  }, [optimizedPlanData, onUpdatePlan, handleClose]);

  if (!isOpen) return null;

  return (
    <Modal
      title="Mathematical Optimizer"
      open={isOpen}
      onClose={handleCancel}
      scrollContainer="viewport"
      closeLabel="Close"
      aria-describedby="optimizer-modal-description"
      bodySpacingInset="400"
      width="700px">
      <Column spacing="400">
        {/* Description */}
        <Box type="outline" spacingInset="300" style={{ borderLeft: '4px solid #ff9800', background: '#fff8f0' }}>
          <Column spacing="100">
            <Text type={TEXTS.T2} weight="bold">
              About Mathematical Optimization
            </Text>
            <Text type={TEXTS.T3} color="secondary">
              {hasPresets
                ? 'This feature uses advanced optimization algorithms to automatically plan your cycle, replacing manual heuristic adjustments with data-driven recommendations. Select a preset below and click Calculate to generate an optimized plan.'
                : 'This feature uses advanced optimization algorithms to automatically plan your cycle, replacing manual heuristic adjustments with data-driven recommendations.'}
            </Text>
          </Column>
        </Box>

        {/* Created plan id — shown once the optimizer plan exists, through creation and rendering */}
        {optimizedPlanId && (
          <Text type={TEXTS.T3} color="secondary">
            {`Plan ID: ${optimizedPlanId}`}
            <CopyToClipboardButton value={optimizedPlanId} />
          </Text>
        )}

        {/* Preset Selection — only shown when presets are configured */}
        {hasPresets && (
          <Box type="outline" spacingInset="400" style={{ backgroundColor: 'white' }}>
            <Column spacing="300">
              {presets.some((p) => p.enabled) && (
                <>
                  <Text type={TEXTS.T2} weight="bold">
                    ⚡ Quick Presets
                  </Text>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '8px'
                    }}>
                    {presets.map((preset) => {
                      const isSelected = selectedPreset === preset.value;
                      const isDisabled = !preset.enabled;
                      return (
                        <button
                          key={preset.value}
                          onClick={() => !isDisabled && setSelectedPreset(preset.value)}
                          disabled={isDisabled}
                          aria-pressed={isSelected}
                          style={{
                            padding: '8px 10px',
                            borderRadius: '6px',
                            border: `2px solid ${isSelected ? '#2ecc71' : '#e0e0e0'}`,
                            background: isDisabled ? '#f5f5f5' : isSelected ? '#f0faf0' : 'white',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.6 : 1,
                            transition: 'all 0.2s',
                            textAlign: 'left'
                          }}>
                          <Text type={TEXTS.T3} weight="bold">
                            {preset.label}
                          </Text>
                          <Text type={TEXTS.T4} color="secondary">
                            {preset.description}
                            {isDisabled && ' (Coming soon)'}
                          </Text>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              <Row alignmentHorizontal="center" spacingInsetTop="200">
                <Button type="primary" onClick={handleCalculate} disabled={isCalculating}>
                  {isCalculating ? 'Calculating...' : 'Calculate Optimized Plan'}
                </Button>
              </Row>
            </Column>
          </Box>
        )}

        {/* Loading */}
        {isCalculating && (
          <Column alignmentHorizontal="center" spacing="200" spacingInset="400">
            <Loader size="large" />
            <Text type={TEXTS.T3} color="secondary">
              Creating math optimizer plan
            </Text>
          </Column>
        )}

        {/* Error */}
        {error && (
          <Box type="outline" spacingInset="300" style={{ borderLeft: '4px solid #d13212', background: '#fef0ef' }}>
            <Text type={TEXTS.T3} color="error">
              {error}
            </Text>
          </Box>
        )}

        {/* Comparison Table */}
        {comparisonData && !isCalculating && (
          <Box type="outline" spacingInset="400" style={{ borderLeft: '4px solid #4caf50', borderRadius: '6px' }}>
            <Column spacing="300">
              <Row alignmentHorizontal="justify" alignmentVertical="center">
                <Row spacing="300" alignmentVertical="center">
                  <Text type={TEXTS.T2} weight="bold">
                    Plan Comparison
                  </Text>
                  <Select value={selectedCycle} onChange={setSelectedCycle} width={160} size="small">
                    <SelectOption value="total" label="All Cycles (Total)" />
                    {(comparisonData.current.cycleNames || []).map((cn) => (
                      <SelectOption key={cn} value={cn} label={cn} />
                    ))}
                  </Select>
                </Row>
                <Text type={TEXTS.T4} color="secondary" style={{ fontStyle: 'italic' }}>
                  ✅ = Improvement | ⚠️ = Trade-off
                </Text>
              </Row>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#232f3e', color: 'white' }}>
                      <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, minWidth: '160px' }}>
                        Metric
                      </th>
                      <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, minWidth: '120px' }}>
                        Current Plan
                      </th>
                      <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 600, minWidth: '140px' }}>
                        Optimized Plan
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARISON_METRICS.map((metric) => {
                      const currentData =
                        selectedCycle === 'total'
                          ? comparisonData.current.total
                          : comparisonData.current.cycles[selectedCycle] || {};
                      const optimizedData =
                        selectedCycle === 'total'
                          ? comparisonData.optimized.total
                          : comparisonData.optimized.cycles[selectedCycle] || {};
                      const currentVal = currentData[metric.key] || 0;
                      const optimizedVal = optimizedData[metric.key] || 0;
                      const change =
                        currentVal !== 0
                          ? (((optimizedVal - currentVal) / currentVal) * 100).toFixed(1)
                          : optimizedVal !== 0
                          ? 'N/A'
                          : '0.0';
                      const changeNum = change === 'N/A' ? 0 : parseFloat(change);
                      const improved = metric.lowerIsBetter ? optimizedVal < currentVal : optimizedVal > currentVal;
                      const worsened = metric.lowerIsBetter ? optimizedVal > currentVal : optimizedVal < currentVal;
                      const bgColor = improved ? '#e8f5e9' : worsened ? '#fef0ef' : '#f9f9f9';
                      const emoji = improved ? ' ✅' : worsened ? ' ⚠️' : '';
                      const changeColor = improved ? '#2e7d32' : worsened ? '#d13212' : '#666';
                      const changeDisplay = change === 'N/A' ? 'N/A' : `${changeNum > 0 ? '+' : ''}${change}%`;
                      return (
                        <tr key={metric.key} style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 600, color: '#232f3e' }}>{metric.label}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'center', backgroundColor: '#f9f9f9' }}>
                            {currentVal.toLocaleString()}
                          </td>
                          <td
                            style={{
                              padding: '10px 14px',
                              textAlign: 'center',
                              backgroundColor: bgColor,
                              fontWeight: 600
                            }}>
                            <span>{optimizedVal.toLocaleString()}</span>
                            <span style={{ marginLeft: '8px', fontSize: '12px', color: changeColor }}>
                              {emoji} {changeDisplay}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Column>
          </Box>
        )}
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" spacing="200">
          <Button type="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          {comparisonData && !isCalculating && (
            <Button type="primary" onClick={handleUpdatePlan}>
              Update Plan
            </Button>
          )}
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default MathematicalOptimizerModal;
